<template>
  <q-dialog v-model="isDialog" persistent position="bottom">
    <q-card style="width: 460px; max-width: 95vw">
      <q-linear-progress :value="progress" color="pink" />

      <q-card-section>
        <q-linear-progress
          dark
          stripe
          rounded
          size="20px"
          :value="progress"
          color="red"
          class="q-mt-sm"
        >
          <div class="absolute-full flex flex-center">
            <q-badge color="white" text-color="accent" :label="progressLabel" />
          </div>
        </q-linear-progress>

        <div class="upload-status q-mt-md text-body2 text-weight-medium">
          {{ statusLabel }}
        </div>

        <div class="upload-metric q-mt-xs text-caption text-grey-7">
          {{ $t('addMedia.upload.confirmedPhotos') }}:
          {{ confirmedCount }} / {{ totalCount }}
        </div>

        <div class="upload-metric q-mt-xs text-caption text-grey-7">
          {{ $t('addMedia.upload.transferredData') }}:
          {{ formatBytes(uploadedBytes) }} / {{ formatBytes(totalBytes) }}
        </div>

        <div class="upload-metric q-mt-xs text-caption text-grey-7">
          {{ $t('addMedia.upload.speed') }}: {{ speedLabel }}
        </div>

        <div class="upload-metric q-mt-xs text-caption text-grey-7">
          {{ $t('addMedia.upload.activeBatches') }}: {{ activeRequests }}
        </div>

        <div class="upload-queue q-mt-sm text-caption text-grey-7">
          <span>{{ $t('addMedia.upload.pendingPhotos') }}: {{ pendingCount }}</span>
          <span>{{ $t('addMedia.upload.deferredPhotos') }}: {{ deferredCount }}</span>
          <span>{{ $t('addMedia.upload.problemPhotos') }}: {{ failedCount }}</span>
        </div>
      </q-card-section>

      <q-separator />
      <q-card-actions align="right" class="q-px-md q-py-sm">
        <q-btn
          v-if="canPause && !isPaused"
          flat
          color="primary"
          icon="pause"
          :label="$t('addMedia.upload.actions.pause')"
          @click="$emit('pause')"
        />

        <template v-if="canPause && isPaused">
          <q-btn
            color="primary"
            icon="play_arrow"
            :label="$t('addMedia.upload.actions.resume')"
            @click="$emit('resume')"
          />
          <q-btn
            flat
            color="negative"
            :label="$t('addMedia.upload.actions.resumeLater')"
            @click="$emit('sendLater')"
          />
        </template>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
export default {
  name: 'ProgressModal',
  emits: ['pause', 'resume', 'sendLater'],
  props: {
    isProgress: {
      required: false,
      type: Boolean,
      default: false,
    },
    progress: {
      required: true,
      type: Number,
    },
    status: {
      required: false,
      type: String,
      default: 'idle',
    },
    speedBps: {
      required: false,
      type: Number,
      default: 0,
    },
    confirmedCount: {
      required: false,
      type: Number,
      default: 0,
    },
    totalCount: {
      required: false,
      type: Number,
      default: 0,
    },
    activeRequests: {
      required: false,
      type: Number,
      default: 0,
    },
    pendingCount: {
      required: false,
      type: Number,
      default: 0,
    },
    deferredCount: {
      required: false,
      type: Number,
      default: 0,
    },
    failedCount: {
      required: false,
      type: Number,
      default: 0,
    },
    uploadedBytes: {
      required: false,
      type: Number,
      default: 0,
    },
    totalBytes: {
      required: false,
      type: Number,
      default: 0,
    },
    isPaused: {
      required: false,
      type: Boolean,
      default: false,
    },
    canPause: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDialog: false,
    };
  },
  computed: {
    progressLabel() {
      return (Math.min(1, Math.max(0, this.progress)) * 100).toFixed(2) + '%';
    },
    statusLabel() {
      const statusKeys = {
        idle: 'idle',
        preparing: 'preparing',
        uploading: 'uploading',
        stalled: 'stalled',
        waiting: 'waiting',
        'waiting-long': 'waitingLong',
        retrying: 'retrying',
        deferred: 'deferred',
        offline: 'offline',
        pausing: 'pausing',
        paused: 'paused',
        stopping: 'stopping',
        failed: 'failed',
        completed: 'completed',
      };

      const statusKey = statusKeys[this.status] || 'idle';
      return this.$t(`addMedia.upload.status.${statusKey}`);
    },
    speedLabel() {
      const speed = Math.max(0, Number(this.speedBps) || 0);
      const mb = speed / (1024 * 1024);

      if (mb >= 1) {
        return `${mb.toFixed(1)} MB/s`;
      }

      return `${(speed / 1024).toFixed(0)} KB/s`;
    },
  },
  methods: {
    formatBytes(value) {
      const bytes = Math.max(0, Number(value) || 0);
      if (bytes >= 1024 ** 3) {
        return `${(bytes / (1024 ** 3)).toFixed(2)} GB`;
      }
      if (bytes >= 1024 ** 2) {
        return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
      }
      if (bytes >= 1024) {
        return `${(bytes / 1024).toFixed(0)} KB`;
      }
      return `${bytes.toFixed(0)} B`;
    },
  },
  watch: {
    isProgress: {
      immediate: true,
      handler(val) {
        this.isDialog = val;
      },
    },
  },
};
</script>

<style scoped>
.upload-status {
  min-height: 40px;
}

.upload-metric {
  min-height: 18px;
  line-height: 18px;
}

.upload-queue {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  min-height: 18px;
}
</style>
